import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@frontegg/react';
import { Loader2, Plus, Trash2, Users } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService from '@/services/tenantConfig.service';

const SUFFIX_REGEX = /^[A-Za-z][A-Za-z0-9_]*$/;

export default function SubtenantsTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserEmail = user?.email || '';
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Form state
  const [tenantName, setTenantName] = useState('');
  const [subtenantSuffix, setSubtenantSuffix] = useState('');
  const [suffixTouched, setSuffixTouched] = useState(false);
  const [adminEmails, setAdminEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [isProvisioning, setIsProvisioning] = useState(false);

  // Validation
  const suffixError =
    subtenantSuffix && !SUFFIX_REGEX.test(subtenantSuffix)
      ? 'Must start with a letter. Only letters, numbers, and underscores allowed.'
      : '';

  const { data: subtenantsData, isLoading } = useQuery({
    queryKey: ['subtenants', page],
    queryFn: () => TenantConfigService.getInstance().listSubtenants(page, 20),
    staleTime: 30 * 1000,
  });

  const resetForm = useCallback(() => {
    setTenantName('');
    setSubtenantSuffix('');
    setSuffixTouched(false);
    setAdminEmails(currentUserEmail ? [currentUserEmail] : []);
    setNewEmail('');
  }, [currentUserEmail]);

  // Auto-derive suffix from tenant name while user hasn't manually edited it
  const deriveSuffix = (name: string) =>
    name.toUpperCase().replace(/[^A-Z0-9_]/g, '_').replace(/^[^A-Z]*/, '').replace(/_+/g, '_').replace(/_$/, '');

  const handleTenantNameChange = useCallback((value: string) => {
    setTenantName(value);
    if (!suffixTouched) {
      setSubtenantSuffix(deriveSuffix(value));
    }
  }, [suffixTouched]);

  const addEmail = useCallback(() => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (adminEmails.includes(email)) return;
    setAdminEmails((prev) => [...prev, email]);
    setNewEmail('');
  }, [newEmail, adminEmails]);

  const removeEmail = useCallback((email: string) => {
    setAdminEmails((prev) => prev.filter((e) => e !== email));
  }, []);

  const handleProvision = useCallback(async () => {
    if (!tenantName.trim() || !subtenantSuffix.trim() || suffixError) return;

    setIsProvisioning(true);
    try {
      await TenantConfigService.getInstance().provisionSubtenant({
        tenant_name: tenantName.trim(),
        subtenant_suffix: subtenantSuffix.trim(),
        admin_emails: adminEmails,
      });

      showToast.success('Subtenant created successfully');
      queryClient.invalidateQueries({ queryKey: ['subtenants'] });
      setDialogOpen(false);
      resetForm();
    } catch (err: any) {
      showToast.error('Failed to create subtenant', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setIsProvisioning(false);
    }
  }, [tenantName, subtenantSuffix, suffixError, adminEmails, queryClient, resetForm]);

  const totalPages = subtenantsData?.total_pages || 1;
  const records = subtenantsData?.records || [];

  return (
    <div className="max-w-4xl space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Subtenants</CardTitle>
              <CardDescription>
                Create and manage subtenants that share your hosted Snowflake
                account. Each subtenant gets its own isolated database and
                resources.
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Subtenant
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : records.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <Users className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                No subtenants yet. Create one to get started.
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Contact Email</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <TableRow
                      key={record.tenant_uuid}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() =>
                        navigate(`/settings/subtenants/${record.tenant_uuid}`)
                      }
                    >
                      <TableCell className="font-medium">
                        {record.name}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {record.slug}
                      </TableCell>
                      <TableCell className="text-sm">
                        {record.contact_email || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {record.created_at
                          ? new Date(record.created_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({subtenantsData?.total_records}{' '}
                    total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Subtenant Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!isProvisioning) {
            setDialogOpen(open);
            if (!open) resetForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl p-8">
          {isProvisioning ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">Setting up subtenant...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  This may take a minute. Creating tenant, configuring Snowflake
                  resources, and running setup scripts.
                </p>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Create Subtenant</DialogTitle>
                <DialogDescription>
                  Set up a new subtenant with its own Snowflake database and
                  resources.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Tenant Name */}
                <div className="space-y-2">
                  <Label htmlFor="tenant-name">Tenant Name</Label>
                  <Input
                    id="tenant-name"
                    value={tenantName}
                    onChange={(e) => handleTenantNameChange(e.target.value)}
                    placeholder="e.g. Acme Corp"
                  />
                </div>

                {/* Subtenant Suffix */}
                <div className="space-y-2">
                  <Label htmlFor="subtenant-suffix">Subtenant Suffix</Label>
                  <Input
                    id="subtenant-suffix"
                    value={subtenantSuffix}
                    onChange={(e) => {
                      setSuffixTouched(true);
                      setSubtenantSuffix(e.target.value.toUpperCase());
                    }}
                    placeholder="e.g. ACME"
                    className="font-mono uppercase"
                  />
                  {suffixError ? (
                    <p className="text-sm text-destructive">{suffixError}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Used for Snowflake resource names (e.g. PLIABLE_DB_
                      {subtenantSuffix || 'SUFFIX'})
                    </p>
                  )}
                </div>

                {/* Admin Emails */}
                <div className="space-y-2">
                  <Label>Admin Emails</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="admin@example.com"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addEmail();
                        }
                      }}
                    />
                    <Button
                      variant="secondary"
                      onClick={addEmail}
                      type="button"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {adminEmails.length > 0 && (
                    <div className="space-y-1 mt-2">
                      {adminEmails.map((email) => (
                        <div
                          key={email}
                          className="flex items-center justify-between bg-muted rounded px-3 py-1.5 text-sm"
                        >
                          <span>{email}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeEmail(email)}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground">
                    These users will be invited as admins of the new subtenant.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleProvision}
                  disabled={
                    !tenantName.trim() || !subtenantSuffix.trim() || !!suffixError
                  }
                >
                  Create Subtenant
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
